import React, { createContext, useState, useEffect, useContext } from 'react'
import { getMyOrganization } from '@api/auth'
import Loader from '@components/common/Loader/Loader'

import dictionary from '../data/dictionary'
import { useAuth } from '@contexts/auth'
import { localize } from '@utils/localize'
import { useTranslation as i18useTranslation } from 'react-i18next'

// Create the context with default values
const TranslationContext = createContext({
  lang: 'en',
  setLang: () => {},
  dictionary: {},
  country: 'UK',
  t: key => key
})

export const TranslationProvider = ({ children }) => {
  const { isAuthenticated } = useAuth()
  const [lang, setLang] = useState('en')
  const { t, i18n } = i18useTranslation()

  const country = 'FR'

  useEffect(() => {
    const getProfile = async () => {
      if (isAuthenticated) {
        const response = await getMyOrganization()

        if (response?.locale) {
          setLang(response?.locale)
        }
      } else {
        const locale = localStorage.getItem('OOTI-locale')
        if (!locale) {
          const countryCode = await localize()
          let langCode = 'en'
          if (
            countryCode === 'FR' ||
            countryCode === 'IT' ||
            countryCode === 'ES' ||
            countryCode === 'PT' ||
            countryCode === 'DE' ||
            countryCode === 'NL'
          )
            langCode = countryCode.toLowerCase()
          setLang(langCode || 'en')
        } else setLang(locale || 'en')
      }
    }
    getProfile()
  }, [isAuthenticated])

  useEffect(() => {
    i18n.changeLanguage(lang)
  }, [lang, i18n])

  if (!lang) return <Loader />

  // Make sure the dictionary for the current language is defined and non-empty
  const currentDictionary = dictionary[lang] || {}

  // Return our provider with the current language and translation function
  return (
    <TranslationContext.Provider
      value={{
        lang,
        setLang,
        dictionary: currentDictionary,
        country,
        // Translation function that ensures a string is always returned
        t: (key, options) => {
          // If we have a key in the legacy dictionary and no options are provided, use it
          if (currentDictionary[key] && !options) {
            const result = currentDictionary[key]
            // Ensure the result is a string
            if (typeof result === 'object') {
              console.warn(
                `Translation key "${key}" returned an object instead of a string`
              )
              return key // Return the key as fallback
            }
            return result
          }

          // Otherwise use i18next
          const result = t(key, options)
          // Ensure the result is a string
          if (typeof result === 'object') {
            console.warn(
              `Translation key "${key}" returned an object instead of a string`,
              result
            )
            return key // Return the key as fallback
          }
          return result
        }
      }}
    >
      {children}
    </TranslationContext.Provider>
  )
}

export const useTranslation = () => useContext(TranslationContext)
